import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { LocationAutocomplete } from '@/components/LocationAutocomplete';
import { useLocationValidation } from '@/hooks/useLocationValidation';
import { Loader2, MapPin } from 'lucide-react';
import { toast } from 'sonner';

interface UpdateProviderLocationProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentAddress: string;
  onUpdate: (locationData: {
    address: string;
    latitude: number;
    longitude: number;
    city?: string;
    state?: string;
    country?: string;
  }) => void;
}

export const UpdateProviderLocation = ({
  open,
  onOpenChange,
  currentAddress,
  onUpdate,
}: UpdateProviderLocationProps) => {
  const [selectedAddress, setSelectedAddress] = useState(currentAddress);
  const [selectedPlaceId, setSelectedPlaceId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const { geocodeAddress } = useLocationValidation();

  const handleUpdate = async () => {
    if (!selectedAddress) {
      toast.error('Please select an address');
      return;
    }

    if (!selectedPlaceId) {
      toast.error('Please select an address from the suggestions');
      return;
    }

    setIsLoading(true);
    try {
      // Use place_id instead of formatted address
      const geocodeResult = await geocodeAddress(selectedPlaceId);
      
      if (!geocodeResult) {
        toast.error('Failed to geocode address');
        return;
      }

      const { latitude, longitude, formatted_address, address_components } = geocodeResult;

      // address_components is now a structured object from the Edge Function
      onUpdate({
        address: formatted_address,
        latitude,
        longitude,
        city: address_components?.city || address_components?.locality || '',
        state: address_components?.state || address_components?.administrative_area_level_1 || '',
        country: address_components?.country || '',
      });

      onOpenChange(false);
    } catch (error: any) {
      console.error('Location update error:', error);
      toast.error(error.message || 'Failed to update location');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-5 h-5 text-primary" />
            Update Business Location
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="address">Business Address</Label>
            <LocationAutocomplete
              value={selectedAddress}
              onChange={(address, placeId) => {
                setSelectedAddress(address);
                setSelectedPlaceId(placeId || '');
              }}
              placeholder="Enter your business address..."
              type="address"
            />
            <p className="text-xs text-muted-foreground">
              This will be your primary business location displayed to customers
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button onClick={handleUpdate} disabled={isLoading}>
            {isLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Update Location
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
